// UI-specific telemetry for V2.0 features
// Tracks user interactions with new UI components

class UIMetrics {
  constructor() {
    this.enabled = false;
    this.sessionId = this.generateSessionId();
    this.metrics = [];
    this.featureUsage = new Map();
  }

  enable() {
    this.enabled = true;
    if (process.env.NODE_ENV !== 'production') {
      console.log('UI telemetry enabled');
    }
  }

  disable() {
    this.enabled = false;
  }

  generateSessionId() {
    return `ui_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Track compact mode usage
  trackCompactModeUsage(component, enabled, action = 'toggle') {
    if (!this.enabled) return;

    this.record('compact_mode_usage', {
      component, // 'notes_list' or 'project_cards'
      enabled,
      action,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Update feature usage counter
    const key = `compact_${component}`;
    this.featureUsage.set(key, (this.featureUsage.get(key) || 0) + 1);
  }

  // Track navigation order changes
  trackNavigationUsage(version, action, item = null) {
    if (!this.enabled) return;

    this.record('navigation_usage', {
      version, // 'v1' or 'v2'
      action, // 'click', 'today_redirect', 'item_access'
      item, // navigation item clicked
      timestamp: Date.now()
    });
  }

  // Track voice recording removal impact
  trackVoiceComposerUsage(action, alternative = null) {
    if (!this.enabled) return;

    this.record('voice_composer_usage', {
      action, // 'voice_hidden', 'link_clicked', 'alternative_used'
      alternative, // 'voices_page', 'project_recording', etc.
      timestamp: Date.now()
    });
  }

  // Track keyboard navigation in compact modes
  trackKeyboardNavigation(component, key, success) {
    if (!this.enabled) return;

    this.record('keyboard_navigation', {
      component, // 'notes_table', 'projects_table'
      key, // 'ArrowDown', 'Enter', 'Delete', etc.
      success,
      timestamp: Date.now()
    });
  }

  // Track context menu usage
  trackContextMenuUsage(component, action, method = 'mouse') {
    if (!this.enabled) return;

    this.record('context_menu_usage', {
      component,
      action, // 'edit', 'delete', 'move_to_project', etc.
      method, // 'mouse', 'keyboard'
      timestamp: Date.now()
    });
  }

  // Track performance of compact vs card views
  trackViewPerformance(component, mode, renderTime, itemCount) {
    if (!this.enabled) return;

    this.record('view_performance', {
      component,
      mode, // 'compact', 'cards'
      renderTime,
      itemCount,
      itemsPerMs: Math.round(itemCount / renderTime * 100) / 100,
      performanceGrade: this.gradeRenderTime(renderTime, itemCount)
    });
  }

  // Track density achievements (items visible above fold)
  trackViewportDensity(component, mode, visibleItems, viewportHeight) {
    if (!this.enabled) return;

    const density = visibleItems / (viewportHeight / 100); // items per 100px

    this.record('viewport_density_ui', {
      component,
      mode,
      visibleItems,
      viewportHeight,
      density,
      achievedTarget: visibleItems >= 20, // Target from spec
      timestamp: Date.now()
    });
  }

  // Track search forwarding effectiveness
  trackSearchForwarding(fromComponent, toComponent, query, resultCount) {
    if (!this.enabled) return;

    this.record('search_forwarding', {
      fromComponent, // 'header_search'
      toComponent, // 'command_bar'
      query: query.substring(0, 20), // Limit PII
      resultCount,
      successful: resultCount > 0,
      timestamp: Date.now()
    });
  }

  record(eventType, data) {
    if (!this.enabled) return;

    const metric = {
      session_id: this.sessionId,
      timestamp: Date.now(),
      page_path: window.location.pathname,
      ...data
    };

    this.metrics.push(metric);

    // Log for development - can be enhanced later
    try {
      // Simple console logging for now
      if (process.env.NODE_ENV === 'development') {
        console.log(`[UIMetrics] ${eventType}:`, metric);
      }
    } catch (error) {
      console.warn('Failed to log metric:', error);
    }

    // Keep only last 50 metrics in memory
    if (this.metrics.length > 50) {
      this.metrics = this.metrics.slice(-50);
    }

    // Store in localStorage with expiration
    this.persistMetrics();
  }

  gradeRenderTime(ms, itemCount) {
    const msPerItem = ms / itemCount;
    if (msPerItem < 1) return 'excellent';
    if (msPerItem < 2) return 'good';
    if (msPerItem < 5) return 'fair';
    return 'poor';
  }

  persistMetrics() {
    try {
      const stored = JSON.parse(localStorage.getItem('ui.metrics') || '[]');
      const combined = [...stored, ...this.metrics.slice(-10)];
      
      // Add expiration (7 days from now)
      const expiration = Date.now() + (7 * 24 * 60 * 60 * 1000);
      const payload = {
        metrics: combined.slice(-100), // Keep max 100
        expires: expiration
      };
      
      localStorage.setItem('ui.metrics', JSON.stringify(payload));
    } catch (e) {
      // Storage full or unavailable
      console.warn('Failed to persist UI metrics:', e);
    }
  }

  getMetrics() {
    try {
      const stored = localStorage.getItem('ui.metrics');
      if (!stored) return { session: this.metrics, stored: [] };

      const payload = JSON.parse(stored);
      
      // Check expiration
      if (payload.expires && Date.now() > payload.expires) {
        localStorage.removeItem('ui.metrics');
        return { session: this.metrics, stored: [] };
      }

      return {
        session: this.metrics,
        stored: payload.metrics || []
      };
    } catch (e) {
      return { session: this.metrics, stored: [] };
    }
  }

  // Generate usage summary for V2.0 features
  getSummary() {
    const { session, stored } = this.getMetrics();
    const allMetrics = [...session, ...stored];

    const summary = {
      totalEvents: allMetrics.length,
      compactModeUsage: this.summarizeCompactModeUsage(allMetrics),
      navigationUsage: this.summarizeNavigationUsage(allMetrics),
      keyboardUsage: this.summarizeKeyboardUsage(allMetrics),
      performanceStats: this.summarizePerformance(allMetrics),
      featureAdoption: Object.fromEntries(this.featureUsage)
    };

    return summary;
  }

  summarizeCompactModeUsage(metrics) {
    const compactEvents = metrics.filter(m => m.eventType === 'compact_mode_usage');
    const notesCompact = compactEvents.filter(m => m.component === 'notes_list');
    const cardsCompact = compactEvents.filter(m => m.component === 'project_cards');

    return {
      total: compactEvents.length,
      notesListUsage: notesCompact.length,
      projectCardsUsage: cardsCompact.length,
      enabledRatio: compactEvents.filter(e => e.enabled).length / Math.max(compactEvents.length, 1)
    };
  }

  summarizeNavigationUsage(metrics) {
    const navEvents = metrics.filter(m => m.eventType === 'navigation_usage');
    const v2Usage = navEvents.filter(m => m.version === 'v2').length;
    const todayRedirects = navEvents.filter(m => m.action === 'today_redirect').length;

    return {
      total: navEvents.length,
      v2Usage,
      todayRedirects,
      adoptionRate: v2Usage / Math.max(navEvents.length, 1)
    };
  }

  summarizeKeyboardUsage(metrics) {
    const keyEvents = metrics.filter(m => m.eventType === 'keyboard_navigation');
    const successRate = keyEvents.filter(e => e.success).length / Math.max(keyEvents.length, 1);

    return {
      total: keyEvents.length,
      successRate,
      mostUsedKeys: this.getTopKeys(keyEvents)
    };
  }

  summarizePerformance(metrics) {
    const perfEvents = metrics.filter(m => m.eventType === 'view_performance');
    const avgRenderTime = this.calculateAverage(perfEvents, 'renderTime');
    const avgItemsPerMs = this.calculateAverage(perfEvents, 'itemsPerMs');

    return {
      avgRenderTime,
      avgItemsPerMs,
      excellentPerformance: perfEvents.filter(e => e.performanceGrade === 'excellent').length
    };
  }

  getTopKeys(keyEvents) {
    const keyCounts = {};
    keyEvents.forEach(event => {
      keyCounts[event.key] = (keyCounts[event.key] || 0) + 1;
    });
    
    return Object.entries(keyCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([key, count]) => ({ key, count }));
  }

  calculateAverage(metrics, field) {
    if (metrics.length === 0) return null;
    const values = metrics.filter(m => m[field] !== undefined);
    if (values.length === 0) return null;
    
    const sum = values.reduce((acc, m) => acc + m[field], 0);
    return Math.round((sum / values.length) * 100) / 100;
  }

  // Clear all UI metrics
  clear() {
    this.metrics = [];
    this.featureUsage.clear();
    localStorage.removeItem('ui.metrics');
  }
}

// Singleton instance
const uiMetrics = new UIMetrics();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.uiMetrics = uiMetrics;
}

export default uiMetrics;